import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  useCreateOneOnOne,
  useReplaceOpenOneOnOne,
} from '@/shared/hooks/useOneOnOne';

interface CreateMeetingParams {
  employeeId: string;
  managerId: string;
  companyId: string;
  employeeName: string;
}

export const useCreateOneOnOneWithReplace = () => {
  const { t } = useTranslation('oneOnOne');
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [replaceDialogState, setReplaceDialogState] =
    useState<CreateMeetingParams | null>(null);

  const createMutation = useCreateOneOnOne();
  const replaceMutation = useReplaceOpenOneOnOne();

  const createMeeting = async (params: CreateMeetingParams) => {
    setIsCreating(true);

    try {
      const newOneOnOne = await createMutation.mutateAsync({
        employeeId: params.employeeId,
        managerId: params.managerId,
        companyId: params.companyId,
      });

      toast.success(t('oneOnOneCreated'), {
        description: t('oneOnOneCreatedDescription'),
      });

      setIsCreating(false);

      navigate({
        to: '/one-on-one/$id/goals-review',
        params: { id: newOneOnOne._id },
      });

      return newOneOnOne;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        setReplaceDialogState(params);
        return;
      }

      toast.error(t('failedToCreate'), {
        description: t('couldNotCreateOneOnOne'),
      });

      setIsCreating(false);

      return;
    }
  };

  const confirmReplace = async () => {
    if (!replaceDialogState) return;

    try {
      const newOneOnOne = await replaceMutation.mutateAsync({
        employeeId: replaceDialogState.employeeId,
        managerId: replaceDialogState.managerId,
        companyId: replaceDialogState.companyId,
      });

      toast.success(t('meetingReplaced'), {
        description: t('meetingReplacedDescription'),
      });

      setReplaceDialogState(null);

      navigate({
        to: '/one-on-one/$id/goals-review',
        params: { id: newOneOnOne._id },
      });

      return newOneOnOne;
    } catch (error) {
      console.error('Failed to replace open meeting:', error);
      toast.error(t('failedToReplace'), {
        description: t('couldNotReplaceMeeting'),
      });
      throw error;
    }
  };

  const cancelReplace = () => {
    setReplaceDialogState(null);
    setIsCreating(false);
  };

  return {
    createMeeting,
    confirmReplace,
    cancelReplace,
    isCreating,
    isReplacing: replaceMutation.isPending,
    replaceDialogState,
  };
};
